import { api } from "../api";
import type {
  ReportCreate,
  ReportUpdate,
  SessionReport,
} from "../../types/domain";

interface PresignUploadResponse {
  upload_url: string;
  file_key: string;
  file_name: string;
  max_size_mb: number;
}

export const reportService = {
  getForSession: (sessionId: string) =>
    api.get<SessionReport[]>(`/sessions/${sessionId}/reports`),

  getSessionCounts: (sessionIds: string[]) => {
    if (sessionIds.length === 0) {
      return Promise.resolve({} as Record<string, number>);
    }
    const query = new URLSearchParams();
    sessionIds.forEach((sessionId) => query.append("session_ids", sessionId));
    return api.get<Record<string, number>>(`/reports/session-counts?${query}`);
  },

  getById: (id: string) => api.get<SessionReport>(`/reports/${id}`),

  create: (sessionId: string, data: ReportCreate) =>
    api.post<SessionReport>(`/sessions/${sessionId}/reports`, data),

  update: (id: string, data: ReportUpdate) =>
    api.patch<SessionReport>(`/reports/${id}`, data),

  /**
   * Upload a file using presigned URL flow:
   * 1. Request presigned PUT URL from backend
   * 2. Upload file directly to storage via PUT
   * 3. Confirm upload with backend
   */
  uploadFile: async (reportId: string, fieldId: string, file: File) => {
    // Step 1: Get presigned upload URL
    const presign = await api.post<PresignUploadResponse>(
      `/reports/${reportId}/fields/${fieldId}/upload`,
      {
        file_name: file.name,
        content_type: file.type || "application/octet-stream",
      }
    );

    // Step 2: Upload directly to storage
    const uploadResp = await fetch(presign.upload_url, {
      method: "PUT",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!uploadResp.ok) {
      throw new Error("reportSubmit.uploadFailed");
    }

    // Step 3: Confirm upload
    return api.post<{
      id: string;
      file_key: string;
      file_name: string;
      url: string;
    }>(`/reports/${reportId}/fields/${fieldId}/confirm-upload`, {
      file_key: presign.file_key,
      file_name: presign.file_name,
    });
  },

  downloadUrl: (reportId: string, fieldId: string, valueId?: string) =>
    api.get<{ url: string; file_name: string }>(
      `/reports/${reportId}/fields/${fieldId}/download${
        valueId ? `?value_id=${valueId}` : ""
      }`
    ),

  getThumbnailUrl: (reportId: string, fieldId: string, valueId?: string) =>
    api.get<{ url: string; file_name: string }>(
      `/reports/${reportId}/fields/${fieldId}/download${
        valueId ? `?value_id=${valueId}` : ""
      }`
    ),

  /**
   * Delete one uploaded file from a MULTIPLE_FILES field.
   */
  deleteFile: (reportId: string, fieldId: string, valueId: string) =>
    api.delete<void>(`/reports/${reportId}/fields/${fieldId}/files/${valueId}`),
};
